import TextField from '@mui/material/TextField';
import { KeyboardEvent, useEffect, useState } from 'react';
import { DeleteIcon, MinusIcon, PlusIcon } from '../../icons';
import { FlexBox, FlexBoxProps } from '../layout';

const MAX_QUANTITY = 20000;

type NumberInputProps = FlexBoxProps & {
  initialValue: number;
  disabled?: boolean;
  onChange?: (value: number) => void;
  onDelete?: () => void;
  deleteIcon?: React.ReactNode;
  decrementIcon?: React.ReactNode;
  incrementIcon?: React.ReactNode;
};

const NumberInput: React.FC<NumberInputProps> = ({
  initialValue,
  disabled,
  onChange,
  onDelete,
  deleteIcon,
  decrementIcon,
  incrementIcon,
  ...props
}) => {
  const [value, setValue] = useState(
    Math.min(Math.max(initialValue, 0), MAX_QUANTITY)
  );
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [lastValidValue, setLastValidValue] = useState(value);

  useEffect(
    () => setValue(Math.min(Math.max(initialValue, 0), MAX_QUANTITY)),
    [initialValue]
  );

  const handleIncrement = () =>
    !disabled &&
    setValue((prev) => {
      const newValue = Math.min(prev + 1, MAX_QUANTITY);
      onChange && onChange(newValue);
      return newValue;
    });

  const handleDecrement = () =>
    !disabled &&
    setValue((prev) => {
      const newValue = Math.max(prev - 1, 1);
      onChange && onChange(newValue);
      return newValue;
    });

  const handleDelete = () => !disabled && onDelete && onDelete();

  const startEditing = () => {
    if (!disabled) {
      setInputValue(value.toString());
      setIsEditing(true);
    }
  };

  const finishEditing = () => {
    const newValue = parseInt(inputValue, 10);
    if (!isNaN(newValue) && newValue >= 0) {
      setValue(newValue);
      setLastValidValue(newValue);
      onChange && onChange(newValue);
    } else {
      setValue(lastValidValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      finishEditing();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setValue(lastValidValue);
    }
  };

  return (
    <FlexBox
      alignItems="center"
      justifyContent={'center'}
      gap={1}
      bgcolor={(theme) =>
        disabled ? theme.palette.gray?.[50] : theme.palette.gold?.[50]
      }
      color={(theme) =>
        disabled ? theme.palette.gray?.[300] : theme.palette.gold?.[700]
      }
      height={'2.5rem'}
      width={'10rem'}
      borderRadius={6}
      py={0.5}
      px={1}
      {...props}
    >
      <FlexBox
        width={'1.5rem'}
        height={'1.5rem'}
        onClick={value > 1 || !onDelete ? handleDecrement : handleDelete}
        cursor={disabled ? 'default' : 'pointer'}
      >
        {value > 1 || !onDelete
          ? (decrementIcon ?? <MinusIcon />)
          : (deleteIcon ?? <DeleteIcon />)}
      </FlexBox>
      <FlexBox
        width={'3rem'}
        alignItems={'center'}
        justifyContent={'center'}
        sx={{ userSelect: 'none' }}
      >
        {isEditing ? (
          <TextField
            variant="standard"
            inputMode="numeric"
            slotProps={{
              input: {
                inputProps: {
                  style: {
                    width: '100%',
                    textAlign: 'center',
                    padding: '2px',
                  },
                },
              },
            }}
            type="number"
            value={inputValue}
            onChange={(e) =>
              setInputValue(e.target.value.replace(/^0+(?=\d)/, ''))
            }
            onBlur={finishEditing}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        ) : (
          <FlexBox
            onClick={startEditing}
            justifyContent={'center'}
            cursor={disabled ? 'default' : 'pointer'}
            userSelect={'none'}
          >
            {value}
          </FlexBox>
        )}
      </FlexBox>
      <FlexBox
        width={'1.5rem'}
        height={'1.5rem'}
        onClick={handleIncrement}
        cursor={disabled ? 'default' : 'pointer'}
      >
        {incrementIcon ?? <PlusIcon />}
      </FlexBox>
    </FlexBox>
  );
};

export default NumberInput;
